import type { match } from 'react-router';
import { generatePath, matchPath } from 'react-router';

import type { Location } from 'history';

import { getHumanLabelID, isCategoryLabel } from '@proton/mail/helpers/location';
import type { CategoryLabelID } from '@proton/shared/lib/constants';
import { MAILBOX_LABEL_IDS } from '@proton/shared/lib/constants';
import { changeSearchParams, getSearchParams } from '@proton/shared/lib/helpers/url';
import { isNumber } from '@proton/shared/lib/helpers/validators';
import { HUMAN_TO_LABEL_IDS, LABEL_IDS_TO_HUMAN } from '@proton/shared/lib/mail/constants';
import type { Filter, SearchParameters, Sort } from '@proton/shared/lib/mail/search';

import { MAIN_ROUTE_PATH } from '../constants';

// No interface to comply with generatePath argument type
export type MailUrlParams = {
    labelID: string;
    elementID?: string;
    messageID?: string;
};

export const getUrlPathname = (params: MailUrlParams) =>
    generatePath(MAIN_ROUTE_PATH, {
        ...params,
        // The as any is needed du to a bug into ExtractRouteParams inference which takes the `?` in the param name
        // Remove this once fixed
        labelID: getHumanLabelID(params.labelID),
    } as any);

export const setParamsInLocation = (location: Location, params: MailUrlParams): Location => ({
    ...location,
    pathname: getUrlPathname(params),
});

export const getParamsFromPathname = (pathname: string): match<MailUrlParams> =>
    matchPath(pathname, { path: MAIN_ROUTE_PATH }) as match<MailUrlParams>;

const stringToPage = (string: string | undefined): number => {
    if (string === undefined) {
        return 0;
    }
    const pageNumber = parseInt(string, 10);
    if (!Number.isNaN(pageNumber)) {
        return pageNumber - 1;
    }
    return 0;
};

/**
 * The sort type and the sort order needs to be updated based on the labelID.
 *
 * The following rules apply:
 * - If the labelID is SNOOZED or INBOX, the sort type is changed to SnoozeTime.
 * - If the labelID is SNOOZED the sort order is reversed.
 * - If the labelID is SCHEDULED, the sort order is reversed as well.
 *
 * In both SNOOZE and SCHEDULE labelID we want to display the elements that will be sent or unsnoozed first.
 * This change is invisible for the user and only affects the sorting logic.
 */
const stringToSort = (string: string | undefined, labelID?: string): Sort => {
    const isSnooze = labelID === MAILBOX_LABEL_IDS.SNOOZED;
    const isScheduledLabel = labelID === MAILBOX_LABEL_IDS.SCHEDULED;
    const isSnoozeOrInbox = labelID === MAILBOX_LABEL_IDS.INBOX || labelID === MAILBOX_LABEL_IDS.SNOOZED;

    switch (string) {
        case '-size':
            return { sort: 'Size', desc: true };
        case 'size':
            return { sort: 'Size', desc: false };
        case 'date': {
            const desc = !!isScheduledLabel;
            return {
                sort: isSnoozeOrInbox ? 'SnoozeTime' : 'Time',
                desc: isSnooze ? !desc : desc,
            };
        }
        default: {
            const desc = !isScheduledLabel;
            return {
                sort: isSnoozeOrInbox ? 'SnoozeTime' : 'Time',
                desc: isSnooze ? !desc : desc,
            };
        }
    }
};

const stringToInt = (string: string | undefined): number | undefined => {
    if (string === undefined) {
        return undefined;
    }
    return isNumber(string) ? parseInt(string, 10) : undefined;
};

export const sortToString = (sort: Sort): string | undefined => {
    if (sort.sort === 'SnoozeTime' || sort.sort === 'Time') {
        return !sort.desc ? 'date' : undefined;
    }
    return sort.desc ? '-size' : 'size';
};

const stringToFilter = (string: string | undefined): Filter => {
    switch (string) {
        case 'read':
            return { Unread: 0 };
        case 'unread':
            return { Unread: 1 };
        case 'has-file':
            return { Attachments: 1 };
        default:
            return {};
    }
};

type FilterStatus = 'has-file' | 'unread' | 'read';
export const filterToString = (filter: Filter): FilterStatus | undefined => {
    if (filter.Attachments === 1) {
        return 'has-file';
    }

    if (filter.Unread === 1) {
        return 'unread';
    }

    if (filter.Unread === 0) {
        return 'read';
    }

    return undefined;
};

export const keywordToString = (keyword: string): string | undefined => {
    const trimmed = keyword.trim();
    return trimmed || undefined;
};

export const pageFromUrl = (location: Location) => stringToPage(getSearchParams(location.hash).page);

export const sortFromUrl = (location: Location, labelID?: string) =>
    stringToSort(getSearchParams(location.hash).sort, labelID);

export const filterFromUrl = (location: Location) => stringToFilter(getSearchParams(location.hash).filter);

export const categoryIDFromUrl = (location: Location): CategoryLabelID | undefined => {
    const humanCategory = getSearchParams(location.hash).category;
    if (!humanCategory) {
        return undefined;
    }

    const category = HUMAN_TO_LABEL_IDS[humanCategory];
    if (category && isCategoryLabel(category)) {
        return category as CategoryLabelID;
    }

    return undefined;
};

export const extractSearchParameters = (location: Location): SearchParameters => {
    const { address, from, to, keyword, begin, end, wildcard } = getSearchParams(location.hash);
    return {
        address,
        from,
        to,
        keyword,
        begin: stringToInt(begin),
        end: stringToInt(end),
        wildcard: stringToInt(wildcard),
    };
};

export const setPageInUrl = (location: Location, page: number) =>
    changeSearchParams(location.pathname, location.hash, { page: page === 0 ? undefined : String(page + 1) });

export const setSortInUrl = (location: Location, sort: Sort) =>
    changeSearchParams(location.pathname, location.hash, { page: undefined, sort: sortToString(sort) });

export const setFilterInUrl = (location: Location, filter: Filter) =>
    changeSearchParams(location.pathname, location.hash, { page: undefined, filter: filterToString(filter) });

export const resetFilterAndSort = (location: Location) =>
    changeSearchParams(location.pathname, location.hash, { page: undefined, filter: undefined, sort: undefined });

export const resetFilter = (location: Location) =>
    changeSearchParams(location.pathname, location.hash, { filter: undefined });

export const resetSort = (location: Location) =>
    changeSearchParams(location.pathname, location.hash, { sort: undefined });

const INBOX_PATHNAME = `/${LABEL_IDS_TO_HUMAN[MAILBOX_LABEL_IDS.INBOX]}`;

// Categories are only present in inbox, so always link to inbox regardless of current location
export const setCategoryInUrl = (category: CategoryLabelID) => {
    return changeSearchParams(INBOX_PATHNAME, '', {
        category: LABEL_IDS_TO_HUMAN[category],
    });
};

// Drops any category from the URL, used when category access is lost
export const getInboxUrl = () => INBOX_PATHNAME;

export const getInboxRedirectUrl = ({
    isCategoryViewEnabled,
    isCategoryViewEnabledSettled,
}: {
    isCategoryViewEnabled: boolean;
    isCategoryViewEnabledSettled: boolean;
}): string | null => {
    if (!isCategoryViewEnabledSettled) {
        return null;
    }

    return isCategoryViewEnabled ? setCategoryInUrl(MAILBOX_LABEL_IDS.CATEGORY_DEFAULT) : getInboxUrl();
};
